import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  TrendingUp,
  DollarSign,
  Users,
  Clock,
  Target,
  FileText,
  PieChart,
  Activity,
  AlertTriangle,
  CheckCircle,
  Calendar,
  Download,
  Settings,
  ArrowRight,
  Eye,
  Filter,
  RefreshCw
} from 'lucide-react';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';

const ProjectReportsLanding = () => {
  const navigate = useNavigate();

  const reportCards = [
    {
      id: 'project-dashboard-reports',
      title: 'Project Dashboard Reports',
      description: 'Comprehensive overview of all project activities and performance metrics',
      icon: <BarChart3 size={24} />,
      path: '/reports/project-dashboard-standalone',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Project overview', 'Budget analysis', 'Progress tracking', 'Status distribution']
    },
    {
      id: 'performance-reports',
      title: 'Performance Reports',
      description: 'Project and team performance metrics with detailed analytics',
      icon: <TrendingUp size={24} />,
      path: '/reports/performance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Performance metrics', 'Team productivity', 'Timeline analysis', 'Milestone completion']
    },
    {
      id: 'financial-reports',
      title: 'Financial Reports',
      description: 'Budget analysis, cost tracking, and financial performance insights',
      icon: <DollarSign size={24} />,
      path: '/reports/financial',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Budget vs actual', 'ROI analysis', 'Cost breakdown', 'Billing milestones']
    },
    {
      id: 'resource-utilization',
      title: 'Resource Utilization Reports',
      description: 'Team allocation, time tracking, and resource capacity analysis',
      icon: <Users size={24} />,
      path: '/reports/resource-utilization',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Team allocation', 'Time tracking', 'Capacity planning', 'Skill utilization']
    },
    {
      id: 'analytics-insights',
      title: 'Analytics & Insights',
      description: 'AI-powered analytics, trend analysis, and predictive insights',
      icon: <PieChart size={24} />,
      path: '/reports/analytics',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Trend analysis', 'Predictive analytics', 'Benchmarking', 'Anomaly detection']
    },
    {
      id: 'custom-reports',
      title: 'Custom Reports',
      description: 'Create and manage custom reports for specific business needs',
      icon: <FileText size={24} />,
      path: '/reports/custom',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Executive summaries', 'Compliance reports', 'Stakeholder reports', 'Operational metrics']
    },
    {
      id: 'automated-reports',
      title: 'Automated Reports',
      description: 'Scheduled and real-time automated report generation',
      icon: <Clock size={24} />,
      path: '/reports/automated',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Scheduled reports', 'Real-time dashboards', 'Alert reports', 'Summary reports']
    },
    {
      id: 'risk-analysis',
      title: 'Risk Analysis Reports',
      description: 'Risk assessment, mitigation tracking, and issue management reports',
      icon: <AlertTriangle size={24} />,
      path: '/reports/risk-analysis',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Risk assessment', 'Issue tracking', 'Mitigation planning', 'Risk distribution']
    },
    {
      id: 'milestone-reports',
      title: 'Milestone Reports',
      description: 'Milestone tracking, deliverable management, and progress analysis',
      icon: <Target size={24} />,
      path: '/reports/milestones',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Milestone tracking', 'Deliverable status', 'Progress analysis', 'Timeline reports']
    },
    {
      id: 'team-performance',
      title: 'Team Performance Reports',
      description: 'Team productivity, collaboration metrics, and performance insights',
      icon: <Activity size={24} />,
      path: '/reports/team-performance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Team productivity', 'Collaboration metrics', 'Performance insights', 'Skill analysis']
    },
    {
      id: 'compliance-reports',
      title: 'Compliance Reports',
      description: 'Regulatory compliance, policy adherence, and audit trail reports',
      icon: <CheckCircle size={24} />,
      path: '/reports/compliance',
      iconBg: '#FFF9E6',
      iconLine: 'var(--gold-primary)',
      features: ['Regulatory compliance', 'Policy adherence', 'Audit trails', 'Compliance monitoring']
    },
    {
      id: 'executive-summary',
      title: 'Executive Summary Reports',
      description: 'High-level executive dashboards and strategic project insights',
      icon: <BarChart3 size={24} />,
      path: '/reports/executive-summary',
      iconBg: '#D6EFFF',
      iconLine: 'var(--gold-primary)',
      features: ['Executive dashboards', 'Strategic insights', 'High-level metrics', 'Decision support']
    }
  ];

  const handleCardClick = (path) => {
    // Open report in a new window for full-width display
    const reportWindow = window.open(
      path,
      '_blank',
      'width=1920,height=1080,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no'
    );

    // Focus the new window
    if (reportWindow) {
      reportWindow.focus();
    }
  };

  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Project Reports"
        description="Comprehensive reporting and analytics for project management insights"
      />
      <div className="page-container">
        {/* Project Reports Cards */}
        <div className="grid-modern-3">
          {reportCards.map((card) => (
            <div
              key={card.id}
              className="asset-landing-card"
              onClick={() => handleCardClick(card.path)}
            >
              {/* Top Accent Line */}
              <div
                style={{
                  height: '4px',
                  background: 'var(--gold-primary)',
                  width: '100%'
                }}
              />

              {/* Card Content */}
              <div style={{ padding: '24px' }}>
                {/* Icon and Title */}
                <div className="d-flex align-items-center mb-3">
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      background: '#FFF9E6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginRight: '16px'
                    }}
                  >
                    <div style={{ color: 'var(--gold-primary)' }} className="asset-card-icon">
                      {card.icon}
                    </div>
                  </div>
                  <div className="flex-grow-1">
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      margin: '0',
                      fontFamily: 'var(--font-family)'
                    }}>
                      {card.title}
                    </h4>
                  </div>
                  <ArrowRight
                    size={20}
                    className="asset-card-arrow"
                  />
                </div>

                {/* Description */}
                <p style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  marginBottom: '16px',
                  lineHeight: '1.5'
                }}>
                  {card.description}
                </p>

                {/* Key Features */}
                <div style={{ marginBottom: '16px' }}>
                  {card.features.map((feature, index) => (
                    <span
                      key={index}
                      style={{
                        display: 'inline-block',
                        background: '#f3f4f6',
                        color: '#374151',
                        fontSize: '12px',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontWeight: '500',
                        marginRight: '6px',
                        marginBottom: '6px',
                        fontFamily: 'var(--font-family)'
                      }}
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default ProjectReportsLanding;
